import { io, type Socket } from 'socket.io-client';
import type {
  ClientToServerEvents,
  ServerToClientEvents,
  PlayerState,
  HitReport,
  MissileLaunchEvent,
  JoinResult,
  Vec3,
} from '@shared/types';

// ============================================================
// Typed Socket.io client wrapper
// ============================================================

export type GameSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

export class SocketClient {
  private socket: GameSocket | null = null;
  private storedUsername = '';
  private storedGameCode: string | null = null;
  private onReconnectingCb: ((attempt: number) => void) | null = null;
  private onReconnectFailedCb: (() => void) | null = null;
  private onReconnectedCb: (() => void) | null = null;

  get id(): string {
    return this.socket?.id ?? '';
  }

  get connected(): boolean {
    return this.socket?.connected ?? false;
  }

  connect(): Promise<string> {
    return new Promise((resolve, reject) => {
      const serverUrl = import.meta.env.VITE_SERVER_URL ?? '';

      this.socket = io(serverUrl, {
        transports: ['websocket'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
      });

      this.socket.on('connect', () => {
        console.log(`[SKYFALL] Connected: ${this.socket!.id}`);
        resolve(this.socket!.id!);
      });

      this.socket.on('connect_error', (err) => {
        console.error('[SKYFALL] Connection failed:', err.message);
        reject(err);
      });

      // Auto-rejoin on reconnect
      this.socket.io.on('reconnect', () => {
        console.log(`[SKYFALL] Reconnected: ${this.socket!.id}`);
        if (this.storedUsername) {
          this.socket?.emit('player:join', this.storedUsername, this.storedGameCode, (result) => {
            if (result.ok) {
              console.log(`[SKYFALL] Re-joined room ${result.gameCode}`);
              this.onReconnectedCb?.();
            }
          });
        }
      });

      this.socket.io.on('reconnect_attempt', (attempt) => {
        console.log(`[SKYFALL] Reconnecting... attempt ${attempt}`);
        this.onReconnectingCb?.(attempt);
      });

      this.socket.io.on('reconnect_failed', () => {
        console.log('[SKYFALL] Reconnection failed');
        this.onReconnectFailedCb?.();
      });
    });
  }

  disconnect(): void {
    this.socket?.disconnect();
    this.socket = null;
  }

  onReconnecting(cb: (attempt: number) => void): void {
    this.onReconnectingCb = cb;
  }

  onReconnectFailed(cb: () => void): void {
    this.onReconnectFailedCb = cb;
  }

  onReconnected(cb: () => void): void {
    this.onReconnectedCb = cb;
  }

  // ── Emit helpers ──────────────────────────────────────────

  join(username: string, gameCode: string | null): Promise<JoinResult> {
    this.storedUsername = username;
    this.storedGameCode = gameCode;
    return new Promise((resolve) => {
      this.socket?.emit('player:join', username, gameCode, (result) => {
        if (result.ok && result.gameCode) {
          this.storedGameCode = result.gameCode;
        }
        resolve(result);
      });
    });
  }

  sendState(state: PlayerState): void {
    this.socket?.volatile.emit('player:state', state);
  }

  sendHitReport(report: HitReport): void {
    this.socket?.emit('player:hitReport', report);
  }

  sendMissileLaunch(event: MissileLaunchEvent): void {
    this.socket?.emit('player:missileLaunch', event);
  }

  sendMissileExplode(missileId: string, position: Vec3): void {
    this.socket?.emit('player:missileExplode', missileId, position);
  }

  sendMidairCollision(otherId: string): void {
    this.socket?.emit('player:midairCollision', otherId);
  }

  sendDroneScore(): void {
    this.socket?.emit('player:droneScore');
  }

  switchTeam(): void {
    this.socket?.emit('player:switchTeam');
  }

  // ── Event listeners ───────────────────────────────────────

  on<E extends keyof ServerToClientEvents>(
    event: E,
    handler: ServerToClientEvents[E],
  ): void {
    this.socket?.on(event, handler as never);
  }

  off<E extends keyof ServerToClientEvents>(
    event: E,
    handler?: ServerToClientEvents[E],
  ): void {
    if (handler) {
      this.socket?.off(event, handler as never);
    } else {
      this.socket?.off(event);
    }
  }
}
